import React from 'react';
import { getInventoryItem, getInventoryItems, type InventoryItem } from '../../lib/inventoryApi';

/**
 * D108.3: the human step of D84.6. When a client-invoice line matches several stock items the delivery
 * slip refuses with "Pick the stock item on this line"; this picks one. A pick or a clear reports ONLY the
 * id (the caller writes `inventoryItemId` and nothing else), so name, unit and prices never move.
 * A stored link is named by reading the item; if that read fails the line says so and shows the id's tail.
 * Only ship stock is offered: a slip draws from ship stock, never the pantry (D95.5 divisions).
 */
type Props = {
  inventoryItemId: string;
  disabled: boolean;
  onChange: (inventoryItemId: string) => void;
};

const idOf = (i: InventoryItem) => String(i._id || i.id || '');
const unitOf = (i: InventoryItem) => i.uom?.level1Name || i.unit || '';

// On hand is stored as `stock.united.level1Qty` (models/InventoryItem.js); the server sends the
// stored document, so that is the field read here. Twins share a name (D108.1, D84.6), so the
// label also carries the SKU, or the id's tail when there is none, to tell them apart.
type StoredStock = { stock?: { united?: { level1Qty?: unknown } } };
function onHandOf(i: InventoryItem): number | null {
  const q = (i as InventoryItem & StoredStock).stock?.united?.level1Qty;
  return typeof q === 'number' && Number.isFinite(q) ? q : null;
}

function describe(i: InventoryItem): string {
  const unit = unitOf(i);
  const q = onHandOf(i);
  const onHand = q === null ? '' : `${q}${unit ? ` ${unit}` : ''} on hand`;
  const tag = i.sku ? `SKU ${i.sku}` : idOf(i) ? `id ${idOf(i).slice(-6)}` : '';
  return [i.name, unit && !onHand ? unit : '', onHand, tag].filter(Boolean).join(' - ');
}

export function StockItemPicker({ inventoryItemId, disabled, onChange }: Props) {
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState<InventoryItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [known, setKnown] = React.useState<Record<string, InventoryItem>>({});

  React.useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setFailed(false);
      return;
    }
    let live = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setFailed(false);
      try {
        const res = await getInventoryItems({ q, division: 'ship', page: 1, limit: 8 });
        if (live) setResults(res.items || []);
      } catch {
        if (live) {
          setResults([]);
          setFailed(true);
        }
      } finally {
        if (live) setLoading(false);
      }
    }, 300);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  const [unreadable, setUnreadable] = React.useState('');
  React.useEffect(() => {
    if (!inventoryItemId || known[inventoryItemId]) return;
    let live = true;
    getInventoryItem(inventoryItemId)
      .then((item) => {
        if (!live) return;
        if (item) setKnown((k) => ({ ...k, [inventoryItemId]: item }));
        else setUnreadable(inventoryItemId);
      })
      .catch(() => {
        if (live) setUnreadable(inventoryItemId);
      });
    return () => {
      live = false;
    };
  }, [inventoryItemId, known]);

  const linked = inventoryItemId ? known[inventoryItemId] : undefined;
  const pick = (i: InventoryItem) => {
    const id = idOf(i);
    if (!id) return;
    setKnown((k) => ({ ...k, [id]: i }));
    setQuery('');
    setResults([]);
    onChange(id);
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span aria-label="Linked stock item" className="min-w-0 flex-1 truncate">
          {!inventoryItemId
            ? 'Not linked'
            : linked
              ? describe(linked)
              : unreadable === inventoryItemId
                ? `Linked item could not be read (id ${inventoryItemId.slice(-6)})`
                : 'Reading linked item...'}
        </span>
        {inventoryItemId && (
          <button type="button" disabled={disabled} onClick={() => onChange('')} className="rounded border border-slate-300 px-2 py-0.5 text-xs disabled:opacity-40">
            Clear
          </button>
        )}
      </div>
      <input aria-label="Search stock items" disabled={disabled} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search stock items to link" className="h-8 w-full rounded border border-slate-300 bg-white px-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
      {loading && <div className="text-xs text-slate-500">Searching...</div>}
      {failed && <div className="text-xs text-rose-700">Could not search stock items.</div>}
      {results.length > 0 && (
        <ul className="max-h-40 space-y-1 overflow-auto">
          {results.map((i) => (
            <li key={idOf(i) || i.name}>
              <button type="button" disabled={disabled} onClick={() => pick(i)} className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-left text-xs hover:border-cyan-300 hover:bg-cyan-50">
                {describe(i)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
